import { useEffect } from 'react';
import { useFramework } from '@/framework';
import type { MouseOperate } from '@/plugins/mouse-operate';
import { SmartGuidesPlugin } from '../smart-guides';
import { MOVING_ABSORB_DISTANCE, RESIZING_ABSORB_DISTANCE } from './constants';
import { useMoveSnap } from './hooks/use-move-snap';
import { useResizeSnap } from './hooks/use-resize-snap';
import { useScaleSnap } from './hooks/use-scale-snap';

/** Scale 吸附代码已迁入 smart-guides，但交互继续保持关闭。 */
const ENABLE_SCALE_SNAP = false;

export const useSmartGuidesOperationEvents = () => {
    const framework = useFramework();
    const moveSnap = useMoveSnap();
    const resizeSnap = useResizeSnap();
    const scaleSnap = useScaleSnap();
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');

    useEffect(() => {
        if (!mouseOperate) return;

        const clear = () => {
            moveSnap.resetMoveSnap();
            resizeSnap.resetResizeSnap();
            scaleSnap.resetScaleSnap();
            SmartGuidesPlugin.get(framework)?.clear();
        };

        const cleanups = [
            mouseOperate.onMoveBeforeUpdate((event) => {
                if (!MOVING_ABSORB_DISTANCE) return;
                moveSnap.proxyBeforeMoving(event.layer);
                const result = moveSnap.proxyMoving(event.rawX, event.rawY, event.oldLayer, event.layer);

                if (result.isSnapX) {
                    event.candidates.add({
                        slot: 'move-x',
                        value: result.x,
                        correction: result.x - event.rawX,
                        source: 'smart-guides',
                    });
                }
                if (result.isSnapY) {
                    event.candidates.add({
                        slot: 'move-y',
                        value: result.y,
                        correction: result.y - event.rawY,
                        source: 'smart-guides',
                    });
                }
            }),
            mouseOperate.onMoveAfterUpdate((event) => {
                if (MOVING_ABSORB_DISTANCE) {
                    moveSnap.proxyAfterMoving(event.layer, event.isEnd);
                }
            }),
            mouseOperate.onMoveEnd(clear),
            mouseOperate.onResizeStart((event) => {
                if (RESIZING_ABSORB_DISTANCE) {
                    resizeSnap.proxyBeforeResizing(event.operateType, event.layer);
                }
            }),
            mouseOperate.onResizeBeforeUpdate((event) => {
                if (!RESIZING_ABSORB_DISTANCE) return;
                const result = resizeSnap.proxyResizing({
                    displacementX: event.rawDisplacementX,
                    displacementY: event.rawDisplacementY,
                    oldLayer: event.oldLayer,
                    operateType: event.operateType,
                    direction: event.direction,
                    layer: event.layer,
                });
                if (!result.isSnap) return;

                const value = (event.isChangeHeight ? result.y : result.x) * event.direction;
                event.candidates.add({
                    slot: 'resize-offset',
                    value,
                    correction: value - event.rawOffset,
                    source: 'smart-guides',
                });
            }),
            mouseOperate.onResizeAfterUpdate((event) => {
                if (RESIZING_ABSORB_DISTANCE) {
                    resizeSnap.proxyAfterResizing(event.layer, event.isEnd);
                }
            }),
            mouseOperate.onResizeEnd(clear),
            mouseOperate.onCancel(clear),
        ];

        if (ENABLE_SCALE_SNAP) {
            cleanups.push(
                mouseOperate.onScaleStart((event) => {
                    scaleSnap.proxyBeforeScaling(event.operateType, event.layer);
                }),
            );
        }

        return () => cleanups.forEach((cleanup) => cleanup());
        // 操作控制器的内部状态都保存在 ref 中，只在插件挂载时订阅一次。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
